
import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileSubMenuOpen, setMobileSubMenuOpen] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const isHomePage = location.pathname === '/';
  const isLocationPage = location.pathname === '/location';
  const isTransparent = (isHomePage || isLocationPage) && !isScrolled;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setMobileSubMenuOpen(null);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { 
      path: '/about', 
      label: '회사소개',
      subMenu: [
        { path: '/about/history', label: '연혁' }
      ]
    },
    { path: '/products', label: '제품소개',
      subMenu: [
        { path: '/process/sts-panel', label: 'STS패널형 물탱크' },
        { path: '/process/sts-cylinder', label: 'STS원통형 물탱크' },
        { path: '/process/lining', label: '라이닝 물탱크' }
      ]
    },
    { path: '/gallery', label: '현장사진' },
    { path: '/location', label: '오시는길' },
  ];

  const handleMouseEnter = (label: string) => {
    setActiveDropdown(label);
  };

  const handleMouseLeave = () => {
    setActiveDropdown(null);
  };

  const toggleMobileSubMenu = (label: string) => {
    setMobileSubMenuOpen(mobileSubMenuOpen === label ? null : label);
  };

  const scrollToVisionCeo = () => {
    const el = document.getElementById('vision-ceo');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleAboutClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname === '/') {
      scrollToVisionCeo();
    } else {
      navigate('/');
      setTimeout(() => {
        scrollToVisionCeo();
      }, 400);
    }
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
  };

  // Dynamic color classes based on transparent state
  const textColor = isTransparent ? 'text-white' : 'text-gray-700';
  const textHoverColor = isTransparent ? 'hover:text-teal-300' : 'hover:text-teal-600';
  const underlineColor = isTransparent ? 'bg-teal-300' : 'bg-teal-600';
  const mobileIconColor = isTransparent ? 'text-white' : 'text-gray-900';

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isTransparent
          ? 'bg-transparent'
          : isScrolled
            ? 'bg-white shadow-lg'
            : 'bg-white/95 backdrop-blur-sm'
      }`}>
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-12 h-12 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/06580d5e232d5cb0cebd682b84a80f70.png"
                  alt="삼원테크 로고"
                  className={`w-12 h-12 object-contain transition-all duration-300 group-hover:scale-110 ${isTransparent ? 'brightness-0 invert' : ''}`}
                />
              </div>
              <span className="text-2xl font-bold tracking-tight transition-colors duration-300 leading-tight">
                <span className={`block transition-colors duration-500 ${isTransparent ? 'text-white' : 'text-blue-600'}`}>SAMWON</span>
                <span className={`block transition-colors duration-500 ${isTransparent ? 'text-teal-300' : 'text-emerald-600'}`}>TECH</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {navLinks.map((link) => (
                <div
                  key={link.path}
                  className="relative"
                  onMouseEnter={() => link.subMenu && handleMouseEnter(link.label)}
                  onMouseLeave={handleMouseLeave}
                >
                  {link.label === '회사소개' ? (
                    <button
                      onClick={handleAboutClick}
                      className={`px-5 py-2 text-base font-medium tracking-wide transition-colors duration-300 relative group whitespace-nowrap ${textColor} ${textHoverColor} flex items-center gap-1 cursor-pointer`}
                    >
                      {link.label}
                      {link.subMenu && (
                        <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${activeDropdown === link.label ? 'rotate-180' : ''}`}></i>
                      )}
                      <span
                        className={`absolute -bottom-1 left-0 h-0.5 ${underlineColor} transition-all duration-300 ${
                          location.pathname === link.path || location.pathname.startsWith(link.path + '/') ? 'w-full' : 'w-0 group-hover:w-full'
                        }`}
                      ></span>
                    </button>
                  ) : (
                    <Link
                      to={link.path}
                      className={`px-5 py-2 text-base font-medium tracking-wide transition-colors duration-300 relative group whitespace-nowrap ${textColor} ${textHoverColor} flex items-center gap-1`}
                    >
                      {link.label}
                      {link.subMenu && (
                        <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${activeDropdown === link.label ? 'rotate-180' : ''}`}></i>
                      )}
                      <span
                        className={`absolute -bottom-1 left-0 h-0.5 ${underlineColor} transition-all duration-300 ${
                          location.pathname === link.path || location.pathname.startsWith(link.path + '/') ? 'w-full' : 'w-0 group-hover:w-full'
                        }`}
                      ></span>
                    </Link>
                  )}

                  {/* Dropdown Menu */}
                  {link.subMenu && activeDropdown === link.label && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden"
                    >
                      {link.subMenu.map((subLink) => (
                        <Link
                          key={subLink.path}
                          to={subLink.path}
                          className={`block px-5 py-3 text-sm font-medium transition-colors duration-200 ${
                            location.pathname === subLink.path
                              ? 'bg-teal-50 text-teal-600'
                              : 'text-gray-700 hover:bg-gray-50 hover:text-teal-600'
                          }`}
                        >
                          {subLink.label}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </div>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              className={`lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer z-50 transition-colors duration-300 ${mobileIconColor}`}
              aria-label="Toggle menu"
            >
              <i className={`text-2xl transition-transform duration-300 ${isMobileMenuOpen ? 'ri-close-line rotate-90' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            
            {/* Mobile Menu Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="fixed top-0 right-0 bottom-0 w-80 bg-white shadow-2xl z-40 lg:hidden overflow-y-auto"
            >
              <div className="flex flex-col h-full pt-24 px-6">
                <nav className="flex-1 space-y-1">
                  {navLinks.map((link) => (
                    <div key={link.path}>
                      <div className="flex items-center justify-between">
                        {link.label === '회사소개' ? (
                          <button
                            onClick={handleAboutClick}
                            className={`flex-1 px-4 py-3 rounded-xl text-base font-medium transition-all duration-300 text-left cursor-pointer ${
                              location.pathname === link.path
                                ? 'bg-teal-600 text-white'
                                : 'text-gray-700 hover:bg-teal-50 hover:text-teal-600'
                            }`}
                          >
                            {link.label}
                          </button>
                        ) : (
                          <Link
                            to={link.path}
                            onClick={() => !link.subMenu && setIsMobileMenuOpen(false)}
                            className={`flex-1 px-4 py-3 rounded-xl text-base font-medium transition-all duration-300 ${
                              location.pathname === link.path
                                ? 'bg-teal-600 text-white'
                                : 'text-gray-700 hover:bg-teal-50 hover:text-teal-600'
                            }`}
                          >
                            {link.label}
                          </Link>
                        )}
                        {link.subMenu && (
                          <button
                            onClick={() => toggleMobileSubMenu(link.label)}
                            className="w-10 h-10 flex items-center justify-center cursor-pointer text-gray-700"
                            aria-label="Toggle submenu"
                          >
                            <i className={`ri-arrow-down-s-line text-xl transition-transform duration-300 ${mobileSubMenuOpen === link.label ? 'rotate-180' : ''}`}></i>
                          </button>
                        )}
                      </div>
                      
                      {/* Mobile Submenu */}
                      {link.subMenu && mobileSubMenuOpen === link.label && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="ml-4 mt-1 space-y-1 overflow-hidden"
                        >
                          {link.subMenu.map((subLink) => (
                            <Link
                              key={subLink.path}
                              to={subLink.path}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className={`block px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                                location.pathname === subLink.path
                                  ? 'bg-teal-100 text-teal-700'
                                  : 'text-gray-600 hover:bg-gray-100 hover:text-teal-600'
                              }`}
                            >
                              {subLink.label}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </div>
                  ))}
                </nav>
                
                {/* Footer in mobile menu */}
                <div className="py-6 border-t border-gray-200">
                  <p className="text-xs text-gray-500 text-center">
                    © 2024 SAMWONTECH
                  </p>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
